import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '~/components';
import { CopyButton } from '~/components/CopyButton/CopyButton';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';
import type { ArtistContact } from '~/modules/artist-roster/types';
import { capitalize } from '~/modules/artist-roster/utils/capitalize';

interface ContactCardProps {
  contact: ArtistContact;
}

export function ContactCard({ contact }: ContactCardProps) {
  const { t } = useTranslation('roster');
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const hasContactDetails = !!(contact.email || contact.phone);

  const toggleInfoVisibility = useCallback(() => {
    setIsInfoVisible((prev) => !prev);
  }, []);

  useEffect(() => {
    return () => {
      // Make sure to hide the info when the component unmounts so that it's
      // not visible if the contact info is opened again.
      setIsInfoVisible(false);
    };
  }, []);

  // Have to cover the case where there is no translation for the returned
  // contact_type enum value. See discussion here:
  // https://wmggt.atlassian.net/browse/WMG1-564?focusedCommentId=772647
  let title = t(`contactInfo.contactType.${contact.title}`);

  // If we got the key back from the call to t(), we know there wasn't a valid
  // translation so just use the original back end value.
  if (title.startsWith('contactInfo.contactType')) {
    title = contact.title;
  }

  return (
    <div className="flex flex-col items-start rounded-sm p-md bg-level-04">
      <div className="flex w-full items-center justify-between">
        <div className="flex min-w-0 flex-col">
          <span className="text-body-xs font-bold text-default">
            {capitalize(title)}
          </span>
          <span className="truncate text-body-md">
            {t('contactInfo.fullName', {
              firstName: contact.firstName,
              lastName: contact.lastName,
            })}
          </span>
        </div>
        {hasContactDetails ? (
          <div className="flex">
            <Tooltip
              content={
                isInfoVisible ? t('contactInfo.hide') : t('contactInfo.show')
              }
              delayDuration={100}
              sideOffset={4}
              side="top"
              size="sm"
              triggerAsChild
            >
              <IconButton
                iconType={isInfoVisible ? 'eye-slash' : 'eye'}
                iconVariant="light"
                className="ml-sm duration-300 ease-out text-hover"
                data-testid="toggle-info-icon"
                onClick={toggleInfoVisibility}
                aria-label={
                  isInfoVisible ? t('contactInfo.hide') : t('contactInfo.show')
                }
              />
            </Tooltip>
          </div>
        ) : null}
      </div>
      <div className="flex w-full flex-col text-body-sm">
        {contact.phone ? (
          <div className="group flex items-center justify-between">
            <div
              className={cn('truncate duration-300 ease-out', {
                'blur-none': isInfoVisible,
                'select-none blur-sm': !isInfoVisible,
              })}
              aria-hidden={!isInfoVisible}
            >
              <a
                href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
                className={cn({
                  'underline-offset-2 group-hover:underline': isInfoVisible,
                  'pointer-events-none': !isInfoVisible,
                })}
                rel="noreferrer noopener"
              >
                {contact.phone}
              </a>
            </div>
            <CopyButton
              textToCopy={contact.phone}
              tooltipContent={t('contactInfo.copyNumber')}
              tooltipContentCopied={t('contactInfo.copyNumberCopied')}
              className="text-body-md opacity-none duration-300 ease-out focus:opacity-full group-hover:opacity-full"
            />
          </div>
        ) : null}
        {contact.email ? (
          <div className="group flex h-[24px] items-center justify-between gap-sm">
            <div
              className={cn('truncate duration-300 ease-out', {
                'blur-none': isInfoVisible,
                'select-none blur-sm': !isInfoVisible,
              })}
              aria-hidden={!isInfoVisible}
            >
              <a
                href={`mailto:${contact.email}`}
                className={cn('lowercase', {
                  'underline-offset-2 group-hover:underline': isInfoVisible,
                  'pointer-events-none': !isInfoVisible,
                })}
                target="_blank"
                rel="noreferrer noopener"
              >
                {contact.email}
              </a>
            </div>
            <CopyButton
              textToCopy={contact.email}
              tooltipContent={t('contactInfo.copyEmail')}
              tooltipContentCopied={t('contactInfo.copyEmailCopied')}
              className="text-body-md opacity-none duration-300 ease-out focus:opacity-full group-hover:opacity-full"
            />
          </div>
        ) : null}
        {!hasContactDetails ? <span>{t('contactInfo.noInfo')}</span> : null}
      </div>
    </div>
  );
}

ContactCard.displayName = 'ContactCard';
